import {
  CandidateRoleAssessment,
  CandidateRoleCallStatus,
  CandidateRoleCategory,
  CandidateRoleInterviewBooking,
  CandidateRoleInterviewStatus,
  CandidateRoleInterviewType,
  CandidateRoleReview,
  CandidateRoleReviewStatus,
  CandidateRoleScreeningStatus,
  CandidateRoleTestStatus,
  CandidateRoleVerification,
  CandidateRoleVerificationOutcome,
} from '@internal/api/roles/roles';

export type TestStatus = CandidateRoleAssessment['test']['status'];
export type CallStatus = CandidateRoleAssessment['call']['status'];
export type InterviewStatus = CandidateRoleAssessment['interview']['status'];
export type ScreeningStatus = CandidateRoleAssessment['screening']['status'];

export type RoleAssessment = CandidateRoleAssessment;

export interface CandidateRole {
  id: string;
  contactRoleId?: string | null;
  role: string;
  label: string;
  title: string;
  stack: string;
  pickable: boolean;
  isMain: boolean;
  isVerified: boolean;
  roleCategory: CandidateRoleCategory;
  paused: boolean;
  onHold: boolean;
  review: CandidateRoleReview;
  verification: CandidateRoleVerification;
  assessment: RoleAssessment;
  interviewBooking: CandidateRoleInterviewBooking;
  started: boolean;
}

export type JourneyRole = Pick<
  CandidateRole,
  'assessment' | 'interviewBooking' | 'paused'
>;

export type RoleProgressSource = JourneyRole & {
  isVerified?: boolean;
  review?: CandidateRoleReview;
  verification?: CandidateRoleVerification;
};

export const isRolePaused = (role: JourneyRole): boolean => role.paused;

export const isRoleVerified = (role: RoleProgressSource): boolean =>
  Boolean(role.isVerified) ||
  role.verification?.outcome === CandidateRoleVerificationOutcome.Verified;

export const isVerificationFailed = (role: RoleProgressSource): boolean =>
  role.verification?.outcome === CandidateRoleVerificationOutcome.Failed;

export const hasVerificationOutcome = (role: RoleProgressSource): boolean =>
  isRoleVerified(role) || isVerificationFailed(role);

export const isReviewWaitlisted = (role: RoleProgressSource): boolean =>
  role.review?.status === CandidateRoleReviewStatus.Waitlisted ||
  isRolePaused(role);

export const isReviewPassed = (role: RoleProgressSource): boolean =>
  role.review?.status === CandidateRoleReviewStatus.Passed;

export const isTestCompleted = (role: JourneyRole): boolean =>
  role.assessment.test.status === CandidateRoleTestStatus.Completed ||
  role.assessment.test.status === CandidateRoleTestStatus.Skipped;

export const isCallCompleted = (role: JourneyRole): boolean =>
  role.assessment.call.status === CandidateRoleCallStatus.Skipped ||
  (role.assessment.call.status === CandidateRoleCallStatus.Completed &&
    !role.assessment.call.allowRetake);

export const hasBookedInterview = (role: JourneyRole): boolean =>
  role.assessment.interview.status === CandidateRoleInterviewStatus.Booked ||
  role.assessment.interview.status === CandidateRoleInterviewStatus.Completed;

export const isInterviewCompleted = (role: JourneyRole): boolean =>
  role.assessment.interview.status === CandidateRoleInterviewStatus.Completed;

export const isScreeningCompleted = (role: JourneyRole): boolean =>
  role.assessment.screening.status === CandidateRoleScreeningStatus.Completed;

export const getSentInterviewLink = (role: JourneyRole): string | null =>
  hasBookedInterview(role) ? null : role.assessment.interview.link;

export const getSentScreeningLink = (role: JourneyRole): string | null =>
  role.assessment.screening.status === CandidateRoleScreeningStatus.Pending
    ? role.assessment.screening.link
    : null;

export const isInterviewCleared = (role: JourneyRole): boolean =>
  hasBookedInterview(role) ||
  Boolean(getSentInterviewLink(role)) ||
  canBookInterviewType(role, CandidateRoleInterviewType.HardSkillsInterview);

export const canBookInterviewType = (
  role: JourneyRole,
  interviewType: CandidateRoleInterviewType
): boolean =>
  !isRolePaused(role) &&
  role.interviewBooking.canRequestBookingLink &&
  role.interviewBooking.nextInterviewType === interviewType;

export const canBookScreening = (role: JourneyRole): boolean =>
  (!isRolePaused(role) && Boolean(getSentScreeningLink(role))) ||
  canBookInterviewType(role, CandidateRoleInterviewType.ScreeningCall);

export const canBookHardSkills = (role: JourneyRole): boolean =>
  !hasBookedInterview(role) &&
  ((!isRolePaused(role) && Boolean(getSentInterviewLink(role))) ||
    canBookInterviewType(role, CandidateRoleInterviewType.HardSkillsInterview));

export const formatScheduledSlot = (iso: string): string => {
  const at = new Date(iso);
  const date = at.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
  });
  const time = at.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(at)
    .find(part => part.type === 'timeZoneName')?.value;

  return zone ? `${date}, ${time} · ${zone}` : `${date}, ${time}`;
};

export const formatSlot = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const NO_INTERVIEW_SLOTS_MESSAGE =
  "Sadly, we're at capacity for booking slots in your role track. We had to " +
  "queue your application, but we'll reach out once interviewer calendars " +
  'free up.';

export const NOT_STARTED_INTERVIEW_BOOKING: CandidateRoleInterviewBooking = {
  canRequestBookingLink: false,
  nextInterviewType: null,
};

export const NOT_STARTED_REVIEW: CandidateRoleReview = {
  status: CandidateRoleReviewStatus.None,
};

export const NOT_STARTED_VERIFICATION: CandidateRoleVerification = {
  outcome: CandidateRoleVerificationOutcome.None,
  seniority: null,
};

export const NOT_STARTED_ASSESSMENT: RoleAssessment = {
  test: {
    status: CandidateRoleTestStatus.NotStarted,
    allowRetake: true,
    link: null,
  },
  call: {
    status: CandidateRoleCallStatus.NotStarted,
    errored: false,
    allowRetake: false,
  },
  interview: {
    status: CandidateRoleInterviewStatus.NotBooked,
    bookedAt: null,
    link: null,
  },
  screening: {
    status: CandidateRoleScreeningStatus.None,
    bookedAt: null,
    link: null,
  },
};
